import { Editor as TinyEditor, IAllProps } from '@tinymce/tinymce-react';
import { theme } from 'config/theme';
import { UserType } from 'hooks/useUserData';
import { FC } from 'react';

export type ProfileMention = {
  id: string;
  name: string;
  image: string;
  description: string;
};

type EditorProps = IAllProps & {
  getHashtags?: () => Promise<string[]>;
  getUsers?: () => Promise<(UserType & ProfileMention)[]>;
};

export const initConfig = (getHashtags?: () => Promise<string[]>) => ({
  height: '100%',
  menubar: false,
  format: 'html',
  branding: false,
  statusbar: true,
  resize: true,
  paste_data_images: true,
  paste_webkit_styles: 'all',
  browser_spellcheck: true,
  elementpath: false,
  contextmenu: 'false',
  plugins: [
    'advlist',
    'autolink',
    'lists',
    'charmap',
    'visualblocks',
    'link',
    'powerpaste',
    'mentions',
  ],
  toolbar:
    'fontsize | ' +
    'bold italic underline strikethrough link | forecolor backcolor | alignment | indent bullist numlist | ' +
    'removeformat',
  setup: function (editor) {
    editor.ui.registry.addGroupToolbarButton('alignment', {
      icon: 'align-justify',
      tooltip: 'Alignment',
      items: 'alignleft aligncenter alignright alignjustify',
    });

    editor.on('PastePostProcess', function (e) {
      let content = e?.node?.innerHTML;

      let tempContainer = document.createElement('div');
      tempContainer.innerHTML = content;

      // removing the img's which have unsupported type
      const unsupportedImages = tempContainer?.querySelectorAll(
        'img[alt="Unsupported image type."]',
      );
      unsupportedImages?.forEach((img) => {
        const parentElement = img.parentNode as Element;

        img.remove();

        // If the parent element has no other children, remove it as well
        if (parentElement && parentElement?.childNodes?.length === 0) {
          parentElement?.remove();
        }
      });

      // Get all elements containing img tags
      let elementsWithImg = tempContainer?.querySelectorAll('*:has(img)');

      elementsWithImg?.forEach((element) => {
        let div = document.createElement('div');
        div.style.display = 'flex';
        div.style.alignItems = 'flex-end';

        // Move all child nodes of element to the new <div>
        while (element?.firstChild) {
          div.appendChild(element.firstChild);
        }

        if (element?.parentNode) {
          element.parentNode.replaceChild(div, element);
        }
      });

      e.node.innerHTML = tempContainer?.innerHTML;

      // Suppress the 'Some images failed to import' notification
      const notifications = editor.notificationManager.getNotifications();
      notifications?.forEach((notification) => {
        if (notification?.settings?.text?.includes('Some images failed to import')) {
          notification.close();
        }
      });
    });
    // Add space before the '#' character (this condition added to handle the md format issue)
    editor.on('keydown', function (e) {
      if (e.key === '#') {
        const rng = editor.selection.getRng();
        const textBeforeCursor = rng.startContainer.textContent.slice(0, rng.startOffset);

        if (textBeforeCursor.length === 0 || !/\s$/.test(textBeforeCursor)) {
          editor.insertContent(' ');
        }
      }
    });
    const onAction = (autocompleteApi, rng, value) => {
      editor.selection.setRng(rng);
      editor.execCommand('RemoveFormat');

      // Check if the selected hashtag already exists in the content
      const content = editor.getContent();
      const currentHashtag = `#${value}`;

      if (content.includes(currentHashtag)) {
        autocompleteApi.hide();
        return;
      }

      const hashtag = `<span class='hashtag'>#${value}</span> `;

      editor.insertContent(hashtag);
      autocompleteApi.hide();

      // Clear the autocomplete state when space is pressed after a valid hashtag
      editor.on('keydown', (e) => {
        if (e.key === ' ') {
          const rng = editor.selection.getRng();
          const textBeforeCursor = rng.startContainer.textContent.slice(0, rng.startOffset);

          // Check if the text before the cursor contains a valid hashtag (e.g., "#text")
          const hashtagPattern = /#\w+$/;
          if (hashtagPattern.test(textBeforeCursor)) {
            // Reset pattern so autocomplete won't trigger after space
            autocompleteApi.hide();
          }
        }
      });
    };

    const getMatchedChars = async (pattern) => {
      if (getHashtags) {
        const hashtags = await getHashtags();
        return pattern
          ? hashtags.filter((char) => char.includes(pattern)).slice(0, 10)
          : hashtags.slice(0, 10);
      }

      return [];
    };

    editor.ui.registry.addAutocompleter('specialchars_cardmenuitems', {
      trigger: '#',
      minChars: 0,
      columns: 1,
      highlightOn: ['char_name'],
      onAction: onAction,
      fetch: (pattern) => {
        return new Promise(async (resolve) => {
          const results = (
            await getMatchedChars(pattern.includes('#') ? pattern[-1] : pattern)
          ).map((char) => ({
            type: 'cardmenuitem',
            value: char,
            label: char,
            items: [
              {
                type: 'cardcontainer',
                direction: 'vertical',
                items: [
                  {
                    type: 'cardtext',
                    text: `#${char}`,
                  },
                ],
              },
            ],
          }));
          resolve(results);
        });
      },
    });
  },
  content_style: `
    @import url('https://use.typekit.net/tan5xag.css');

    body {
      font-family: Lato, sans-serif, Helvetica, Arial;
      font-size: 16px;
    }

    p {
      margin: 0 0 8px;
    }

    a {
      color: ${theme.palette.custom.link};
    } 
    
    a:hover {
      text-decoration: underline;
    }

    span.mention {
      background-color: ${theme.palette.custom.aliceBlue};
      color: ${theme.palette.custom.link};
      border-radius: 4px;
      padding: 2px;
    }

    span.hashtag { 
      background-color: ${theme.palette.custom.aliceBlue};
      color: ${theme.palette.custom.link};
      border-radius: 4px;
      padding: 2px;
    }
  `,
  mentions_min_chars: 0,
  mentions_selector: 'span.mention',
  mentions_item_type: 'profile',
  link_default_target: '_blank',
  powerpaste_word_import: 'merge',
  powerpaste_googledocs_import: 'merge',
  powerpaste_html_import: 'merge',
  mentions_menu_complete: (editor: TinyEditor['editor'], userInfo: ProfileMention) => {
    if (editor) {
      const span = editor.getDoc().createElement('span');
      span.className = 'mention';
      span.setAttribute('data-mention-id', userInfo.id);
      span.appendChild(editor.getDoc().createTextNode(`@${userInfo.name}`));
      return span;
    }

    return null;
  },
});

const Editor: FC<EditorProps> = ({ init, getHashtags, getUsers, ...props }) => {
  const mentions_fetch = async (query, success) => {
    const users = (await getUsers?.()) || [];
    const filteredUsers = users.filter((user) => {
      return [user.name, user.username].some((field) =>
        field.toLowerCase().includes(query.term.toLowerCase()),
      );
    });

    // only the top 10 mentions will be shown by default
    success(filteredUsers);
  };

  return (
    <TinyEditor
      apiKey="gq1vzxo8fcb4symry1w5rjy7hlczwgj4k8r4z5xrwnpqp1uf"
      init={{
        ...initConfig(getHashtags),
        ...init,
        ...(getUsers && { mentions_fetch: mentions_fetch }),
      }}
      {...props}
    />
  );
};

export default Editor;
